import React, { useMemo, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

export default function NewHeroSection({ lang }) {
    const [kFoods, setKFoods] = useState([]);
    const [loading, setLoading] = useState(true);

    // 1. 언어 코드 매핑 ('jp' -> 'ja' for DB column consistency)
    const langCode = useMemo(() => {
        if (lang === 'jp') return 'ja';
        return lang || 'ko'; // 기본값 ko
    }, [lang]);

    useEffect(() => {
        fetchTop10KFoods();
    }, []);

    const fetchTop10KFoods = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('k_vocabulary')
                .select('*')
                .eq('category', 'K-Food')
                .order('id', { ascending: true })
                .limit(10);

            if (error) throw error;
            setKFoods(data || []);
        } catch (error) {
            console.error('Error fetching Top 10 K-Foods:', error);
        } finally {
            setLoading(false);
        }
    };

    // 2. 텍스트 결정 로직 (4 Rows)
    const getTexts = (food) => {
        // Row 1: 한글 이름 (무조건 한글)
        const row1 = food.hangul || food.name_ko;

        // Row 2: 의미/단어 (언어별)
        let row2;
        if (langCode === 'en') row2 = food.meaning || food.romanization;
        else if (langCode === 'ja') row2 = food.meaning_jp || food.romanization;
        else if (langCode === 'es') row2 = food.meaning_es || food.romanization;
        else if (langCode === 'ko') row2 = food.romanization; // 한국어일 땐 로마자(영어이름) 표기
        else row2 = food.meaning || food.romanization;

        // Row 3: 해당 언어 설명
        const row3 = food[`desc_${langCode}`] || food.desc_ko;

        // Row 4: 한글 설명 (선택된 언어가 한국어면 영어 설명으로 대체하여 중복 방지)
        const row4 = langCode === 'ko' ? food.desc_en : food.desc_ko;

        return { row1, row2, row3, row4 };
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-black flex items-center justify-center text-white">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 rounded-full border-2 border-transparent border-t-amber-500 border-r-amber-500 animate-spin"></div>
                    <span className="text-sm text-gray-400 font-light tracking-widest">LOADING COLLECTION...</span>
                </div>
            </div>
        );
    }

    return (
        <section className="relative flex justify-center px-4 py-8 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 min-h-[80vh] overflow-hidden">
            {/* 프리미엄 배경 효과 - 고급스러운 톤 */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                {/* 따뜻한 그래디언트 */}
                <div className="absolute top-0 left-0 w-96 h-96 bg-amber-500/8 rounded-full blur-[150px]" />
                <div className="absolute top-1/4 right-1/3 w-96 h-96 bg-rose-500/8 rounded-full blur-[150px]" />
                <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-emerald-500/8 rounded-full blur-[150px]" />
                {/* 세련된 그리드 패턴 */}
                <div className="absolute inset-0 opacity-[0.02] bg-[linear-gradient(rgba(255,255,255,.5)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.5)_1px,transparent_1px)] bg-[length:50px_50px]" />
            </div>


            {/* 좌측 광고 영역 (세련된 미니멀 디자인) - 추가 20% 증가 (누적 약 44%) */}
            <div className="hidden xl:flex flex-col w-[245px] fixed top-32 left-8 items-center justify-center bg-gradient-to-b from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-4 z-20 hover:border-white/20 transition-all duration-500 shadow-lg">
                <div className="w-full h-[276px] bg-white/5 rounded-lg mb-3 flex items-center justify-center text-xs text-gray-600 font-light">Ad Space</div>
            </div>


            {/* 우측 광고 영역 (세련된 미니멀 디자인) - 추가 20% 증가 (누적 약 44%) */}
            <div className="hidden xl:flex flex-col w-[245px] fixed top-32 right-8 items-center justify-center bg-gradient-to-b from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-4 z-20 hover:border-white/20 transition-all duration-500 shadow-lg">
                <div className="w-full h-[276px] bg-white/5 rounded-lg flex items-center justify-center text-xs text-gray-600 font-light">Ad Space</div>
            </div>

            {/* 메인 컨텐츠 영역 */}
            <div className="relative z-10 w-full max-w-[1292px] px-4 flex flex-col items-center">

                {/* 헤더 섹션 - 모던 미니멀 */}
                <div className="text-center mb-12 animate-in fade-in slide-in-from-bottom-8 duration-700">
                    <h1 className="text-5xl md:text-6xl font-black text-white tracking-tight mb-2 leading-tight bg-gradient-to-br from-white via-amber-100 to-amber-200 bg-clip-text text-transparent">
                        Top 10 K-Foods
                    </h1>
                    
                    <p className="text-gray-400 text-base md:text-lg font-light max-w-2xl mx-auto leading-relaxed">
                        {lang === 'ko' ? "한국 문화의 맛을 통해 한글을 배우는 특별한 경험" : "Experience Korean culture through authentic flavors and Korean language"}
                    </p>
                </div>

                <div className="flex flex-col space-y-12 w-full">
                    {kFoods.map((food, index) => {
                        const { row1, row2, row3, row4 } = getTexts(food);

                        return (
                            <div
                                key={food.id}
                                className="flex flex-col md:flex-row items-center gap-8 md:gap-12 group"
                            >
                                {/* 좌측: 음식 사진 - 현대적 카드 */}
                                <div className="w-full md:w-5/12 relative">
                                    <div className="relative">
                                        {/* 백그라운드 그로우 효과 */}
                                        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-2xl blur-2xl opacity-0 group-hover:opacity-100 transition-all duration-700" />
                                        
                                        <div className="relative w-full h-48 md:h-64 rounded-2xl overflow-hidden border border-white/10 shadow-2xl group-hover:border-purple-500/40 transition-all duration-500 backdrop-blur-sm">
                                            <img
                                                src={food.image_url}
                                                alt={row1}
                                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 filter brightness-75 group-hover:brightness-95"
                                                loading="lazy"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                                            
                                            {/* 인덱스 배지 */}
                                            <div className="absolute top-4 left-4 w-12 h-12 bg-gradient-to-br from-amber-500 to-rose-500 rounded-full flex items-center justify-center shadow-lg shadow-amber-500/30 border border-white/20">
                                                <span className="font-black text-white text-lg">{String(index + 1).padStart(2, '0')}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* 우측: 텍스트 - 모던 타이포그래피 */}
                                <div className="w-full md:w-7/12 text-left space-y-6">
                                    {/* 1st Row: Hangul - 그래디언트 텍스트 */}
                                    <div>
                                        <h2 className="text-4xl md:text-5xl font-black text-white leading-tight group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-amber-300 group-hover:to-rose-300 group-hover:bg-clip-text transition-all duration-300 mb-2">
                                            {row1}
                                        </h2>
                                    </div>

                                    {/* 2nd Row: Meaning/Romanization */}
                                    <div className="flex items-center gap-3">
                                        <div className="w-1 h-8 bg-gradient-to-b from-amber-500 to-rose-500 rounded-full" />
                                        <h3 className="text-lg md:text-xl font-serif italic text-amber-300/80 font-light tracking-wide">
                                            {row2}
                                        </h3>
                                    </div>

                                    {/* 구분선 */}
                                    <div className="w-12 h-[1.5px] bg-gradient-to-r from-amber-500 via-rose-500 to-transparent group-hover:w-20 transition-all duration-500" />

                                    {/* 설명 텍스트 */}
                                    <div className="space-y-3 pt-2">
                                        {/* 3rd Row: 타겟 언어 설명 */}
                                        <p className="text-base md:text-lg text-gray-200 font-light leading-relaxed break-keep">
                                            {row3}
                                        </p>

                                        {/* 4th Row: 한글 설명 */}
                                        <p className="text-sm text-gray-500 font-light leading-relaxed italic">
                                            {row4}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* 하단 광고 영역 - 세련된 배너 */}
                <div className="w-full mt-20 mb-10">
                    <div className="bg-gradient-to-r from-white/[0.03] via-amber-500/10 to-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl p-12 flex items-center justify-center min-h-[260px] hover:border-amber-500/30 transition-all duration-500 shadow-xl">
                        <div className="text-center text-gray-400 font-light">
                            <p className="text-sm tracking-widest mb-2 text-gray-500">PREMIUM CONTENT</p>
                            <p className="text-xs text-gray-600">Your ad space here</p>
                        </div>
                    </div>
                </div>

                {/* 세련된 푸터 */}
                <div className="h-16 flex items-center justify-center text-white/30 mt-6">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-[1px] bg-gradient-to-r from-transparent to-white/30" />
                        <span className="tracking-[0.2em] text-[10px] font-light">K-LINGUA QUEST</span>
                        <div className="w-8 h-[1px] bg-gradient-to-l from-transparent to-white/30" />
                    </div>
                </div>
            </div>
        </section>
    );
}
